"use client";

import { useState, useMemo, type FormEvent } from "react";
import type { Vehicle, Location, Extra, BankTransferInfo } from "@/lib/types";
import type { BookingRange } from "@/app/vehicles/[slug]/VehicleDetailClient";
import { calculatePrice, computeBillableDays } from "@/lib/pricing";
import { validateBookingForm } from "@/lib/validation";
import { buildBreakdown, type PriceBreakdown as Breakdown } from "@/lib/pricing-breakdown";
import BookingConfirmation from "./BookingConfirmation";
import LocationPicker from "./LocationPicker";
import ExtrasPicker from "./ExtrasPicker";
import PriceBreakdownView from "./PriceBreakdown";

type PaymentMethod = "cash" | "stripe" | "transfer";

interface BookingFormProps {
  vehicle: Vehicle;
  range: BookingRange | null;
  locations: Location[];
  extras: Extra[];
  initialPickupLocationId?: number | null;
  initialReturnLocationId?: number | null;
  transferEnabled?: boolean;
}

interface BookingResult {
  id: number;
  breakdown: Breakdown;
  dateFrom: string;
  dateTo: string;
  paymentMethod: PaymentMethod;
  payToken: string;
  bankTransfer: BankTransferInfo | null;
}

export default function BookingForm({
  vehicle,
  range,
  locations,
  extras,
  initialPickupLocationId,
  initialReturnLocationId,
  transferEnabled = false,
}: BookingFormProps) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const pickupDefault = locations[0]?.id ?? null;
  const initialPickup =
    initialPickupLocationId != null && locations.some((l) => l.id === initialPickupLocationId)
      ? initialPickupLocationId
      : pickupDefault;
  const initialReturn =
    initialReturnLocationId != null && locations.some((l) => l.id === initialReturnLocationId)
      ? initialReturnLocationId
      : initialPickup;
  const [locationId, setLocationId] = useState<number | null>(initialPickup);
  const [returnLocationId, setReturnLocationId] = useState<number | null>(initialReturn);
  const [extraIds, setExtraIds] = useState<number[]>(() =>
    extras.filter((e) => e.is_default).map((e) => e.id),
  );
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<BookingResult | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("cash");

  const chosenPickup = useMemo(
    () => locations.find((l) => l.id === locationId) ?? null,
    [locations, locationId],
  );

  const chosenReturn = useMemo(
    () => locations.find((l) => l.id === returnLocationId) ?? null,
    [locations, returnLocationId],
  );

  const chosenExtras = useMemo(
    () => extras.filter((e) => extraIds.includes(e.id)),
    [extras, extraIds],
  );

  if (success) {
    return (
      <BookingConfirmation
        vehicle={vehicle}
        dateFrom={success.dateFrom}
        dateTo={success.dateTo}
        breakdown={success.breakdown}
        bookingId={success.id}
        paymentMethod={success.paymentMethod}
        payToken={success.payToken}
        bankTransfer={success.bankTransfer}
      />
    );
  }

  const days = range
    ? computeBillableDays(range.date_from, range.date_to, range.pickup_time, range.return_time)
    : 0;
  const pricePerDay = range ? calculatePrice(days, vehicle).pricePerDay : 0;

  const breakdown = range && chosenPickup && chosenReturn
    ? buildBreakdown({
        pricePerDay,
        days,
        pickup_location: { name: chosenPickup.name, surcharge: chosenPickup.surcharge },
        return_location: { name: chosenReturn.name, surcharge: chosenReturn.surcharge },
        extras: chosenExtras.map((e) => ({ name: e.name, price: e.price })),
        deposit: vehicle.deposit,
      })
    : null;

  // Enable "Rezervuoti" only when every required field is filled and valid —
  // same rules the server enforces (validateBookingForm) plus dates + locations.
  const canSubmit =
    Boolean(range) &&
    consent &&
    locationId != null &&
    returnLocationId != null &&
    validateBookingForm({ client_name: name, client_phone: phone, client_email: email }).valid;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    if (!range) {
      setError("Pasirinkite nuomos datas.");
      setSubmitting(false);
      return;
    }

    if (!locationId || !returnLocationId) {
      setError("Pasirinkite atsiėmimo ir grąžinimo vietas.");
      setSubmitting(false);
      return;
    }

    try {
      const res = await fetch("/api/bookings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          vehicle_id: vehicle.id,
          date_from: range.date_from,
          date_to: range.date_to,
          pickup_time: range.pickup_time,
          return_time: range.return_time,
          client_name: name,
          client_phone: phone,
          client_email: email,
          consent,
          honeypot,
          location_id: locationId,
          return_location_id: returnLocationId,
          extra_ids: extraIds,
          payment_method: paymentMethod,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Įvyko klaida. Bandykite dar kartą.");
        return;
      }

      // Online payment: take the customer straight to Stripe Checkout. If we
      // can't start it, fall through to the confirmation (which offers a retry).
      if (paymentMethod === "stripe") {
        try {
          const co = await fetch("/api/checkout", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ bookingId: data.id, token: data.pay_token }),
          });
          const cod = await co.json();
          if (co.ok && cod.url) {
            window.location.href = cod.url as string;
            return;
          }
        } catch {
          // ignore — show confirmation with retry button below
        }
      }

      setSuccess({
        id: data.id as number,
        breakdown: data.breakdown as Breakdown,
        dateFrom: range.date_from,
        dateTo: range.date_to,
        paymentMethod,
        payToken: data.pay_token as string,
        bankTransfer: (data.bank_transfer as BankTransferInfo | null) ?? null,
      });
    } catch {
      setError("Ryšio klaida. Patikrinkite internetą ir bandykite dar kartą.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="absolute -left-[9999px]" aria-hidden="true">
        <label>
          Do not fill this
          <input
            type="text"
            name="honeypot"
            tabIndex={-1}
            autoComplete="off"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />
        </label>
      </div>

      <LocationPicker
        id="pickup_location"
        label="Atsiėmimo vieta"
        locations={locations}
        value={locationId}
        onChange={setLocationId}
        emptyMessage="Rezervacija laikinai negalima — nenustatytos atsiėmimo vietos."
      />

      <LocationPicker
        id="return_location"
        label="Grąžinimo vieta"
        locations={locations}
        value={returnLocationId}
        onChange={setReturnLocationId}
      />

      <ExtrasPicker
        extras={extras}
        value={extraIds}
        onChange={setExtraIds}
      />

      <h3 className="text-lg font-semibold text-gray-900 pt-2">Rezervacija</h3>

      {!range && (
        <div className="bg-gray-50 rounded-xl p-6 text-center text-gray-500">
          <p>Pasirinkite nuomos datas kalendoriuje, kad galėtumėte rezervuoti.</p>
        </div>
      )}

      {range && (
        <>
          <div className="bg-blue-50 rounded-xl p-4">
            <div className="text-sm text-blue-800 mb-2">
              {range.date_from} {range.pickup_time} &mdash; {range.date_to} {range.return_time}
            </div>
            {breakdown && <PriceBreakdownView breakdown={breakdown} variant="form" />}
          </div>

        <div>
          <label htmlFor="client_name" className="block text-sm font-medium text-gray-700 mb-1">
            Vardas ir pavardė
          </label>
          <input
            id="client_name"
            name="name"
            type="text"
            autoComplete="name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            placeholder="Jonas Jonaitis"
          />
        </div>

        <div>
          <label htmlFor="client_phone" className="block text-sm font-medium text-gray-700 mb-1">
            Telefonas
          </label>
          <input
            id="client_phone"
            name="tel"
            type="tel"
            autoComplete="tel"
            required
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            placeholder="+370 6XX XXXXX"
          />
        </div>

        <div>
          <label htmlFor="client_email" className="block text-sm font-medium text-gray-700 mb-1">
            El. paštas
          </label>
          <input
            id="client_email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            placeholder="jonas@email.lt"
          />
        </div>

        <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            required
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span>
            Sutinku su{" "}
            <a href="/taisykles" target="_blank" rel="noopener" className="text-blue-600 underline hover:text-blue-700">
              paslaugos taisyklėmis
            </a>{" "}
            ir{" "}
            <a href="/privacy" target="_blank" rel="noopener" className="text-blue-600 underline hover:text-blue-700">
              privatumo politika
            </a>
            .
          </span>
        </label>

        <fieldset className="space-y-2">
          <legend className="block text-sm font-medium text-gray-700 mb-1">Apmokėjimo būdas</legend>
          {(
            [
              { value: "cash", title: "Mokėti vietoje", desc: "Grynais arba kortele atsiimant automobilį." },
              { value: "stripe", title: "Apmokėti dabar internetu", desc: "Saugiai kortele per Stripe." },
              {
                value: "transfer",
                title: "Bankinis pavedimas",
                desc: "Pervesite į mūsų sąskaitą; rekvizitus gausite po rezervacijos.",
                hidden: !transferEnabled,
              },
            ] as { value: PaymentMethod; title: string; desc: string; hidden?: boolean }[]
          )
            .filter((o) => !o.hidden)
            .map((o) => (
              <label
                key={o.value}
                className="flex items-start gap-3 p-3 border border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 has-[:checked]:border-blue-500 has-[:checked]:bg-blue-50"
              >
                <input
                  type="radio"
                  name="payment_method"
                  checked={paymentMethod === o.value}
                  onChange={() => setPaymentMethod(o.value)}
                  className="mt-0.5 h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm">
                  <span className="block font-medium text-gray-900">{o.title}</span>
                  <span className="block text-xs text-gray-500 mt-0.5">{o.desc}</span>
                </span>
              </label>
            ))}
        </fieldset>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
            {error}
          </div>
        )}

          <button
            type="submit"
            disabled={submitting || !canSubmit}
            className="w-full py-3 px-4 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitting
              ? paymentMethod === "stripe"
                ? "Nukreipiama į apmokėjimą..."
                : "Siunčiama..."
              : paymentMethod === "stripe"
                ? "Rezervuoti ir apmokėti"
                : "Rezervuoti"}
          </button>
        </>
      )}
    </form>
  );
}
